import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Plan de trésorerie : comment le construire et l\'utiliser vraiment | Trezoone',
    description: 'Construisez un plan de trésorerie solide. Modèle gratuit, exemples concrets pour association et PME. Anticipez vos besoins de cash sur 12 mois.',
    canonical: '/plan-tresorerie',
    keywords: 'plan de trésorerie, plan trésorerie prévisionnel, modèle plan trésorerie, tableau trésorerie, construire plan trésorerie',
  },
  hero: {
    tag: 'Plan de trésorerie',
    title: 'Un plan de trésorerie, ça prend 30 minutes. Et ça vous évite des mois de galère.',
    subtitle: 'Le plan de trésorerie, c\'est la liste de ce qui va rentrer et sortir sur les 12 prochains mois. Simple en théorie. Vital en pratique. Voici comment en faire un qui marche vraiment.',
    ctaLabel: 'Créer mon plan de trésorerie',
  },
  pilierLink: {
    label: 'Guide complet : gestion de trésorerie',
    href: '/gestion-tresorerie',
  },
  sections: [
    {
      type: 'hook',
      title: 'Ce que coûte un plan de trésorerie inexistant',
      content: 'Sans plan, vous découvrez les problèmes quand il est trop tard pour réagir. Voici ce qui arrive :',
      items: [
        'Vous apprenez le 2 du mois que vous ne pouvez pas payer les salaires le 5',
        'Votre banquier vous refuse un découvert car vous n\'avez aucune visibilité documentée',
        'Vous ratez une opportunité d\'investissement faute de cash — alors que vous aviez les moyens 3 mois plus tôt',
      ],
    },
    {
      type: 'explain',
      title: 'Qu\'est-ce qu\'un plan de trésorerie exactement ?',
      content: 'Un plan de trésorerie est un tableau qui liste, mois par mois, tous les encaissements attendus (ce qui va rentrer) et tous les décaissements prévus (ce qui va sortir). À partir de là, on calcule le solde de fin de mois — et on voit si ça tient ou pas.',
      items: [
        'Colonne 1 : Encaissements (ventes, subventions, loyers reçus, remboursements TVA…)',
        'Colonne 2 : Décaissements (salaires, loyers, fournisseurs, charges sociales, remboursements d\'emprunt…)',
        'Ligne finale : Solde = solde début de mois + encaissements - décaissements',
        'Un solde négatif prévu = une tension à financer en avance (découvert négocié, avance, décalage de charge)',
      ],
    },
    {
      type: 'example',
      title: 'Exemple de plan de trésorerie — Association culturelle',
      content: 'Situation : budget annuel 80 000 €, 3 salariés, subventions de la mairie et de la région.',
      items: [
        'Janvier : Charges 7 000 € — Subvention Région attendue mais non versée → Solde -2 000 €',
        'Février : Charges 7 000 € — Subvention Mairie 5 000 € → Solde -4 000 €',
        'Mars : Charges 7 000 € — Versement Région 20 000 € → Solde +9 000 €',
        'Résultat : 2 mois difficiles prévisibles → négociation d\'un découvert autorisé de 5 000 € en novembre',
        'Sans plan : découvert surprise, agios, stress, relation bancaire dégradée',
      ],
    },
    {
      type: 'midcta',
      title: 'Créez votre plan de trésorerie en 30 minutes',
      content: 'Import bancaire automatique · 12 mois de projection · Alertes incluses',
    },
    {
      type: 'checklist',
      title: 'La checklist pour construire votre plan de trésorerie',
      items: [
        'Lister toutes les recettes récurrentes avec leur date de versement réelle (pas comptable)',
        'Identifier les subventions et leurs délais habituels de versement',
        'Lister toutes les charges fixes : salaires, loyers, assurances, remboursements',
        'Ajouter les charges variables : fournisseurs selon vos délais de paiement',
        'Inclure les échéances exceptionnelles : TVA, cotisations annuelles, investissements',
        'Calculer le solde mois par mois à partir de votre solde bancaire actuel',
        'Identifier les mois à solde négatif et prévoir la solution de financement',
        'Mettre à jour le plan chaque mois avec les données réelles',
      ],
    },
    {
      type: 'solution',
      title: 'Trezoone automatise votre plan de trésorerie',
      content: 'Plutôt que de maintenir un fichier Excel fragile, Trezoone construit votre plan de trésorerie à partir de vos données bancaires réelles et de vos prévisions. Résultat : un plan toujours à jour, sans ressaisie.',
      items: [
        'Import automatique de vos transactions bancaires (CSV, OFX, PDF)',
        'Plan de trésorerie prévisionnel sur 12 mois mis à jour en temps réel',
        'Alertes automatiques quand un mois devient critique',
        'Suivi des écarts entre prévisionnel et réalisé',
        'Scénarios de simulation (retard de subvention, hausse des charges)',
        'Export PDF pour vos financeurs, banquiers ou administrateurs',
      ],
    },
    {
      type: 'faq',
      title: 'Questions fréquentes sur le plan de trésorerie',
      faq: [
        {
          q: 'Quelle est la durée idéale d\'un plan de trésorerie ?',
          a: 'En général 12 mois glissants. Pour les structures saisonnières ou avec des cycles longs (secteur agricole, associations avec appels à projets), on peut monter à 18 ou 24 mois.',
        },
        {
          q: 'Quelle différence entre plan de trésorerie et budget prévisionnel ?',
          a: 'Le budget est une vision comptable annuelle (produits et charges). Le plan de trésorerie est une vision cash mensuelle (encaissements et décaissements). Les deux sont complémentaires : le budget dit si vous êtes bénéficiaire, le plan de trésorerie dit si vous avez du cash au bon moment.',
        },
        {
          q: 'Comment gérer les recettes incertaines dans le plan ?',
          a: 'Intégrez-les avec une probabilité. Construisez 2 versions du plan : une avec la recette, une sans. L\'écart entre les deux vous donne le montant de trésorerie à sécuriser en cas de non-réalisation.',
        },
        {
          q: 'À quelle fréquence mettre à jour son plan de trésorerie ?',
          a: 'Chaque mois minimum. Après chaque événement significatif (contrat signé, dépense imprévue, retard de versement). Trezoone le met à jour automatiquement à chaque import bancaire.',
        },
        {
          q: 'Peut-on utiliser Excel pour son plan de trésorerie ?',
          a: 'Oui au départ. Mais Excel ne se connecte pas à votre banque, ne génère pas d\'alertes, et les erreurs de formule sont fréquentes. Un outil comme Trezoone vous fait gagner du temps et évite les oublis.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Plans de trésorerie par profil et par situation',
      links: [
        { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
        { label: 'Prévision de trésorerie', href: '/prevision-tresorerie' },
        { label: 'Logiciel de trésorerie', href: '/logiciel-tresorerie' },
        { label: 'Plan de trésorerie PME', href: '/plan-tresorerie-pme' },
        { label: 'Plan trésorerie association', href: '/plan-tresorerie-association-guide-complet' },
        { label: 'Excel trésorerie association', href: '/excel-tresorerie-association' },
        { label: 'Modèle plan trésorerie', href: '/modele-plan-tresorerie' },
        { label: 'Cash flow prévisionnel', href: '/cash-flow-entreprise' },
        { label: 'Différence trésorerie vs résultat', href: '/difference-tresorerie-resultat' },
      ],
    },
    {
      type: 'cta',
      title: 'Construisez votre plan de trésorerie en 30 minutes',
      content: 'Importez vos données bancaires, définissez vos prévisions, obtenez votre plan sur 12 mois. Essai gratuit sans carte.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
    { label: 'Logiciel de trésorerie', href: '/logiciel-tresorerie' },
    { label: 'Prévision trésorerie (outil)', href: '/fonctionnalites/prevision-tresorerie' },
    { label: 'Simulateur', href: '/simulateur-tresorerie-association' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function PlanTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
